import React, { useState, useRef } from 'react';
import taskAPI from '../api/taskAPI';
import {
  FaFilePdf, FaFileWord, FaFileImage, FaFile,
  FaTrash, FaUpload, FaSpinner, FaDownload, FaEye,
} from 'react-icons/fa';

// ── File type helpers ─────────────────────────────────────────────────────────
const getFileType = (file) => {
  if (file.type.startsWith('image/'))                         return 'image';
  if (file.type === 'application/pdf')                        return 'pdf';
  if (file.type.includes('word') || file.name.endsWith('.doc') || file.name.endsWith('.docx')) return 'doc';
  return 'other';
};

const FileIcon = ({ fileType, className = 'text-2xl' }) => {
  if (fileType === 'image') return <FaFileImage className={`${className} text-blue-500`} />;
  if (fileType === 'pdf')   return <FaFilePdf   className={`${className} text-red-500`} />;
  if (fileType === 'doc')   return <FaFileWord  className={`${className} text-blue-700`} />;
  return <FaFile className={`${className} text-gray-500`} />;
};

const formatSize = (bytes) => {
  if (bytes < 1024)        return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

// ─────────────────────────────────────────────────────────────────────────────
// TaskFileUpload
// Props:
//   taskId        — string, required after task is saved
//   existingFiles — array from task.attachments (shown on edit)
//   onUploaded    — callback(attachments) after successful upload
//   canDelete     — bool, show delete button
// ─────────────────────────────────────────────────────────────────────────────
const TaskFileUpload = ({ taskId, existingFiles = [], onUploaded, canDelete = true }) => {
  const [pendingFiles, setPendingFiles] = useState([]);   // files chosen but not yet uploaded
  const [uploading, setUploading]       = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState(existingFiles);
  const [previewUrl, setPreviewUrl]     = useState(null);
  const inputRef = useRef(null);

  // Convert File to base64
  const toBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload  = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

  const handleFileChange = (e) => {
    const chosen = Array.from(e.target.files);
    const mapped = chosen.map(f => ({
      file:     f,
      name:     f.name,
      size:     f.size,
      fileType: getFileType(f),
      preview:  f.type.startsWith('image/') ? URL.createObjectURL(f) : null,
    }));
    setPendingFiles(prev => [...prev, ...mapped]);
    e.target.value = ''; // reset so same file can be re-selected
  };

  const removePending = (idx) => {
    setPendingFiles(prev => prev.filter((_, i) => i !== idx));
  };

  const handleUpload = async () => {
    if (!taskId) { alert('Save the task first before uploading files.'); return; }
    if (pendingFiles.length === 0) { alert('No files selected.'); return; }

    setUploading(true);
    try {
      const files = await Promise.all(
        pendingFiles.map(async (pf) => ({
          base64:   await toBase64(pf.file),
          name:     pf.name,
          fileType: pf.fileType,
        }))
      );

      const res = await taskAPI.post(`/${taskId}/attachments`, { files });
      setUploadedFiles(res.data.attachments);
      setPendingFiles([]);
      onUploaded?.(res.data.attachments);
      alert(`✅ ${files.length} file(s) uploaded successfully!`);
    } catch (err) {
      console.error('Upload error:', err);
      alert('❌ Upload failed. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (attachmentId) => {
    if (!window.confirm('Delete this attachment?')) return;
    try {
      await taskAPI.delete(`/${taskId}/attachments/${attachmentId}`);
      setUploadedFiles(prev => prev.filter(f => f._id !== attachmentId));
    } catch (err) {
      alert('❌ Failed to delete attachment.');
    }
  };

  return (
    <div className="border border-purple-200 rounded-xl p-4 bg-purple-50 space-y-4">
      <h4 className="font-semibold text-purple-700 text-sm flex items-center gap-2">
        <FaUpload /> Attachments
        <span className="text-xs font-normal text-gray-500">(Images, PDF, DOC — visible to all assignees)</span>
      </h4>

      {/* ── Drop zone ────────────────────────────────────────────────────────── */}
      <div
        onClick={() => inputRef.current?.click()}
        onDragOver={e => e.preventDefault()}
        onDrop={e => {
          e.preventDefault();
          const dropped = Array.from(e.dataTransfer.files);
          const mapped  = dropped.map(f => ({
            file: f, name: f.name, size: f.size,
            fileType: getFileType(f),
            preview: f.type.startsWith('image/') ? URL.createObjectURL(f) : null,
          }));
          setPendingFiles(prev => [...prev, ...mapped]);
        }}
        className="border-2 border-dashed border-purple-300 rounded-lg p-6 text-center cursor-pointer hover:bg-purple-100 transition"
      >
        <FaUpload className="mx-auto text-purple-400 text-2xl mb-2" />
        <p className="text-sm text-gray-500">Click or drag & drop files here</p>
        <p className="text-xs text-gray-400 mt-1">Supports: JPG, PNG, GIF, PDF, DOC, DOCX</p>
        <input
          ref={inputRef}
          type="file"
          multiple
          accept="image/*,.pdf,.doc,.docx,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
          onChange={handleFileChange}
          className="hidden"
        />
      </div>

      {/* ── Pending files (chosen, not yet uploaded) ─────────────────────────── */}
      {pendingFiles.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Ready to upload</p>
          {pendingFiles.map((pf, idx) => (
            <div key={idx} className="flex items-center gap-3 bg-white border rounded-lg px-3 py-2">
              {pf.preview
                ? <img src={pf.preview} alt="" className="w-10 h-10 object-cover rounded" />
                : <FileIcon fileType={pf.fileType} />
              }
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-700 truncate">{pf.name}</p>
                <p className="text-xs text-gray-400">{formatSize(pf.size)}</p>
              </div>
              <button onClick={() => removePending(idx)}
                className="text-red-400 hover:text-red-600 text-sm"><FaTrash /></button>
            </div>
          ))}

          <button
            onClick={handleUpload}
            disabled={uploading}
            className="w-full bg-purple-600 hover:bg-purple-700 disabled:opacity-50 text-white font-semibold py-2 rounded-lg flex items-center justify-center gap-2 transition"
          >
            {uploading
              ? <><FaSpinner className="animate-spin" /> Uploading...</>
              : <><FaUpload /> Upload {pendingFiles.length} file(s)</>
            }
          </button>
        </div>
      )}

      {/* ── Already uploaded files ────────────────────────────────────────────── */}
      {uploadedFiles.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">
            Uploaded ({uploadedFiles.length})
          </p>
          {uploadedFiles.map((f) => (
            <div key={f._id} className="flex items-center gap-3 bg-white border rounded-lg px-3 py-2">
              <FileIcon fileType={f.fileType} />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-700 truncate">{f.name}</p>
                <p className="text-xs text-gray-400">
                  by {f.uploadedBy} · {new Date(f.uploadedAt).toLocaleDateString('en-GB')}
                </p>
              </div>
              <div className="flex items-center gap-2">
                {/* Preview for images */}
                {f.fileType === 'image' && (
                  <button onClick={() => setPreviewUrl(f.url)}
                    className="text-blue-500 hover:text-blue-700 text-sm" title="Preview">
                    <FaEye />
                  </button>
                )}
                {/* Download / open */}
                <a href={f.url} target="_blank" rel="noopener noreferrer"
                  className="text-green-500 hover:text-green-700 text-sm" title="Open / Download">
                  <FaDownload />
                </a>
                {/* Delete */}
                {canDelete && (
                  <button onClick={() => handleDelete(f._id)}
                    className="text-red-400 hover:text-red-600 text-sm" title="Delete">
                    <FaTrash />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {uploadedFiles.length === 0 && pendingFiles.length === 0 && (
        <p className="text-xs text-gray-400 text-center">No attachments yet.</p>
      )}

      {/* ── Image preview modal ───────────────────────────────────────────────── */}
      {previewUrl && (
        <div className="fixed inset-0 z-[9999] bg-black bg-opacity-80 flex items-center justify-center px-4"
          onClick={() => setPreviewUrl(null)}>
          <div className="relative max-w-3xl w-full">
            <button onClick={() => setPreviewUrl(null)}
              className="absolute -top-10 right-0 text-white text-2xl hover:text-red-400">✕</button>
            <img src={previewUrl} alt="preview" className="w-full rounded-xl shadow-2xl" />
          </div>
        </div>
      )}
    </div>
  );
};

export default TaskFileUpload;